/**
 * ใบรับเข้าหนึ่งใบ — `GET /v1/goods-receipts/{receipt_id}`
 *
 * contract ระบุว่า `lot_id` และ `quality_status` เดินทางมากับ receipt อยู่แล้ว
 * **ไม่ต้องยิงคำขอที่สอง** เพราะ receipt ถูกอ่านเพื่อตอบคำถามเดียวคือ "ของเข้าแล้ว
 * หรือยัง และใช้ได้หรือยัง" หน้านี้จึงตอบสองข้อนั้นก่อนรายละเอียดอื่นทั้งหมด
 *
 * หน้านี้ **ไม่มีปุ่มที่เปลี่ยนสถานะคุณภาพ** — `POST /v1/lots/{lot_id}/quality-decision`
 * เป็นงานของ `WO-P1-M4B-002` และยังไม่มีอยู่จริง ปุ่มที่กดแล้วชน `404` จะสอนว่า
 * ของชุดนี้ถูกปล่อยผ่านไปแล้วทั้งที่ยังถูกกักอยู่
 */

import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { GoodsReceiptFailureNotice } from './GoodsReceiptFailureNotice';
import { qualityStatusLabel, qualityStatusTone } from './goodsReceiptView';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { fetchGoodsReceipt, type GoodsReceiptFailure } from '../../lib/goodsReceipts/client';
import { QUALITY_HOLD, type GoodsReceipt } from '../../lib/goodsReceipts/types';

export interface GoodsReceiptDetailViewProps {
  readonly receiptId: string;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
  readonly listHref: string;
}

type DetailState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly receipt: GoodsReceipt }
  | GoodsReceiptFailure;

export function GoodsReceiptDetailView({
  receiptId,
  apiBaseUrl,
  fetchImpl,
  listHref,
}: GoodsReceiptDetailViewProps) {
  const { pathname } = useLocation();
  const [state, setState] = useState<DetailState>({ status: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    setState({ status: 'loading' });

    void fetchGoodsReceipt(receiptId, {
      apiBaseUrl,
      fetchImpl,
      signal: controller.signal,
    }).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(result.status === 'ready' ? { status: 'ready', receipt: result.receipt } : result);
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [receiptId, apiBaseUrl, fetchImpl]);

  const receipt = state.status === 'ready' ? state.receipt : null;

  return (
    <div className="cap-page" data-testid="goods-receipt-detail">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ใบรับเข้า
            <InfoHint
              label="เกี่ยวกับหน้านี้"
              text="ใบรับเข้าหนึ่งใบสร้าง lot หนึ่ง lot — สถานะคุณภาพด้านล่างคือสถานะของ lot นั้น"
            />
          </h1>
        </div>
        <div className="cap-page__actions">
          <Link
            className="cap-button cap-button--ghost"
            data-testid="goods-receipt-detail-list"
            to={{ pathname, search: listHref }}
          >
            กลับไปหน้ารายการ
          </Link>
        </div>
      </div>

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="goods-receipt-detail-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดใบรับเข้า…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <GoodsReceiptFailureNotice failure={state} context="detail" />
      ) : null}

      {receipt === null ? null : (
        <>
          <div
            className="cap-notice"
            data-testid="goods-receipt-quality-hold"
            data-quality-status={receipt.quality_status}
            role="status"
          >
            <StatusBadge tone={qualityStatusTone(receipt.quality_status)}>
              {qualityStatusLabel(receipt.quality_status)}
            </StatusBadge>
            <h2 className="cap-notice__title">
              {receipt.quality_status === QUALITY_HOLD
                ? 'ของกองนี้ยังเบิกไปใช้ไม่ได้'
                : 'สถานะคุณภาพของ lot นี้'}
            </h2>
            <p className="cap-notice__text">
              lot{' '}
              <code className="cap-table__code" data-testid="goods-receipt-lot-id">
                {receipt.lot_id}
              </code>{' '}
              อยู่ในสถานะ{' '}
              <code className="cap-table__code" data-testid="goods-receipt-quality-status">
                {receipt.quality_status}
              </code>
              {receipt.quality_status === QUALITY_HOLD
                ? ' — จำนวนนี้อยู่ในสต๊อกแล้ว แต่ยังเบิกไปผลิตไม่ได้จนกว่าผู้มีสิทธิ์อนุมัติคุณภาพจะตัดสินปล่อยผ่าน และผู้ตัดสินต้องไม่ใช่ผู้รับของเข้า (D-059)'
                : ' — ค่าที่เซิร์ฟเวอร์เก็บไว้ ไม่ใช่ค่าที่หน้าจอนี้กำหนด'}
            </p>
          </div>

          <section className="cap-meta" aria-label="ข้อมูลของใบรับเข้า">
            <div className="cap-meta__item">
              <div className="cap-meta__label">เลขที่ใบรับเข้า</div>
              <div
                className="cap-meta__value cap-table__code"
                data-testid="goods-receipt-receipt-id"
              >
                {receipt.receipt_id}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">สินค้า</div>
              <div className="cap-meta__value cap-table__code">{receipt.product_code}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">โรงงาน (D-004)</div>
              <div className="cap-meta__value cap-table__code">{receipt.plant_code}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ที่เก็บ</div>
              <div className="cap-meta__value cap-table__code">
                {receipt.storage_location_code}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">จำนวนที่รับเข้า</div>
              {/* NUMERIC เป็น string ตลอดเส้นทาง — ไม่จัดรูปแบบซ้ำที่นี่ */}
              <div className="cap-meta__value cap-table__code" data-testid="goods-receipt-quantity">
                {receipt.quantity}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">วันที่ที่มีผล</div>
              <div className="cap-meta__value cap-table__code">{receipt.posting_date}</div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">เลขล็อตของผู้ขาย</div>
              <div className="cap-meta__value cap-table__code">
                {receipt.supplier_lot_id ?? '—'}
              </div>
            </div>
            <div className="cap-meta__item">
              <div className="cap-meta__label">ผู้รับของเข้า</div>
              <div className="cap-meta__value cap-table__code">
                {receipt.created_by_employee_id}
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
